/**
 * Runtime gaps an OLD Android System WebView has that the TRX PayPage Fields
 * bundle (`paypage-fields.trxservices.com/bundle.js`) calls unguarded.
 *
 * MUST be the first import of every entry that loads that bundle
 * (src/embedded.tsx — the POS keyed-entry page — and src/main.tsx). ES module
 * imports evaluate in order, so being first means it runs before anything else
 * in the page, and therefore before the TRX script tag is ever injected.
 * `test/legacyWebViewPolyfills.test.ts` asserts the ordering.
 *
 * Why (2026-10-06, House of Pho): three Elo I-Series 4 tills (Android 10) were
 * factory reset, which puts Android System WebView back to the version baked
 * into the image — Play had kept the never-reset till current. Keyed entry
 * failed on all three with
 *   `TypeError: crypto.randomUUID is not a function at new Pu (…/bundle.js)`
 * — the TRX library constructor calls `crypto.randomUUID()` (Chromium 92+).
 *
 * What the TRX code needs, read from the live bundles on 2026-10-06:
 *   - bundle.js (runs in OUR page, so a polyfill here reaches it):
 *       crypto.randomUUID          Chromium 92  → polyfilled below
 *       Element.replaceChildren()  Chromium 86  → polyfilled below (called on
 *                                                  every field container at init)
 *       Object.fromEntries 73, globalThis 71, attachShadow 53 — fine.
 *       Syntax: ES2018 (object spread) — fine.
 *   - the field IFRAMES (`/static/js/main.*.js`, cross-origin): nothing on this
 *     page can patch them. They use `||=` (logical assignment) and
 *     `String.prototype.replaceAll` — Chromium 85. SYNTAX cannot be polyfilled,
 *     so **85 is the hard floor for keyed entry** whatever we ship; below it the
 *     WebView itself must be updated (Play Store → Android System WebView). Our
 *     own bundle carries `??=` too (React 19 + this entry), and `build.target`
 *     in vite.config.ts pins the floor there.
 *
 * Security: `randomUUID` is built ONLY on `crypto.getRandomValues` (a CSPRNG),
 * exactly as RFC 4122 §4.4 version 4 specifies. If `getRandomValues` is missing
 * nothing is installed — the TRX library then fails loudly, which is correct;
 * a Math.random UUID would be a predictable identifier on a payment page.
 * Nothing is replaced when the platform already provides it.
 */

type CryptoLike = {
    getRandomValues?: <T extends ArrayBufferView | null>(array: T) => T;
    randomUUID?: () => string;
};

const HEX: string[] = Array.from({ length: 256 }, (_, i) => (i + 0x100).toString(16).slice(1));

/** An RFC 4122 v4 UUID from 16 CSPRNG bytes. `cryptoObj.getRandomValues` must exist. */
export function uuidV4From(cryptoObj: Required<Pick<CryptoLike, 'getRandomValues'>>): string {
    const b = new Uint8Array(16);
    // Called as a method: getRandomValues throws "Illegal invocation" unbound.
    cryptoObj.getRandomValues(b);
    b[6] = (b[6] & 0x0f) | 0x40; // version 4
    b[8] = (b[8] & 0x3f) | 0x80; // variant 10xx (RFC 4122)
    return (
        HEX[b[0]] + HEX[b[1]] + HEX[b[2]] + HEX[b[3]] + '-' +
        HEX[b[4]] + HEX[b[5]] + '-' +
        HEX[b[6]] + HEX[b[7]] + '-' +
        HEX[b[8]] + HEX[b[9]] + '-' +
        HEX[b[10]] + HEX[b[11]] + HEX[b[12]] + HEX[b[13]] + HEX[b[14]] + HEX[b[15]]
    );
}

/**
 * Installs `crypto.randomUUID` on `cryptoObj` when it is missing AND a CSPRNG is
 * available. Returns true only when it installed one.
 */
export function installRandomUUID(cryptoObj: CryptoLike | undefined | null): boolean {
    if (!cryptoObj) return false;
    if (typeof cryptoObj.randomUUID === 'function') return false;
    if (typeof cryptoObj.getRandomValues !== 'function') return false;
    const source = cryptoObj as Required<Pick<CryptoLike, 'getRandomValues'>>;
    Object.defineProperty(cryptoObj, 'randomUUID', {
        value: function randomUUID(): string {
            return uuidV4From(source);
        },
        configurable: true,
        writable: true,
        enumerable: false,
    });
    return true;
}

/**
 * Installs `replaceChildren` (DOM Living Standard) on each prototype missing
 * it. Returns true when it installed at least one.
 */
export function installReplaceChildren(protos: ReadonlyArray<object | undefined>): boolean {
    let installed = false;
    for (const proto of protos) {
        if (!proto || typeof (proto as { replaceChildren?: unknown }).replaceChildren === 'function') continue;
        Object.defineProperty(proto, 'replaceChildren', {
            value: function replaceChildren(this: ParentNode & Node, ...nodes: Array<Node | string>): void {
                while (this.lastChild) this.removeChild(this.lastChild);
                if (nodes.length > 0) this.append(...nodes);
            },
            configurable: true,
            writable: true,
            enumerable: false,
        });
        installed = true;
    }
    return installed;
}

/** Installs every polyfill this page needs; returns the names it installed. */
export function installLegacyWebViewPolyfills(scope: typeof globalThis = globalThis): string[] {
    const installed: string[] = [];
    if (installRandomUUID((scope as { crypto?: CryptoLike }).crypto)) installed.push('crypto.randomUUID');
    const s = scope as { Element?: { prototype: object }; DocumentFragment?: { prototype: object }; Document?: { prototype: object } };
    if (installReplaceChildren([s.Element?.prototype, s.DocumentFragment?.prototype, s.Document?.prototype])) {
        installed.push('replaceChildren');
    }
    return installed;
}

/**
 * What this page load had to polyfill — empty on a current WebView. Read by the
 * entries to tag Sentry, so a till on an old WebView is visible without a console.
 */
export const INSTALLED_LEGACY_POLYFILLS: readonly string[] = installLegacyWebViewPolyfills();
